import { Form, FormikHelpers } from 'formik';
import { Plus } from 'lucide-react';

import {
  PorAccessManagementUsersSelector,
  Option,
} from '@/react/portainer/access-control/AccessManagement/PorAccessManagementUsersSelector';
import { isLimitedToBE } from '@/react/portainer/feature-flags/feature-flags.service';
import { RoleTypes } from '@/portainer/rbac/models/role';
import { FeatureId } from '@/react/portainer/feature-flags/enums';

import { LoadingButton } from '@@/buttons';
import { FormControl } from '@@/form-components/FormControl';
import { PortainerSelect } from '@@/form-components/PortainerSelect';

import { CreateAccessValues, Role, Access } from '../types';

interface Props {
  values: CreateAccessValues;
  setFieldValue: FormikHelpers<CreateAccessValues>['setFieldValue'];
  isValid: boolean;
  dirty: boolean;
  availableUsersAndTeams: Access[];
  roles: Role[];
  isLoading: boolean;
  isUpdating: boolean;
  limitedFeature: FeatureId;
}

export function CreateAccessInnerForm({
  values,
  setFieldValue,
  availableUsersAndTeams,
  roles,
  isLoading,
  isUpdating,
  isValid,
  dirty,
  limitedFeature,
}: Props) {
  const limitedToBE = isLimitedToBE(limitedFeature);

  const roleOptions = roles.map((role) => ({
    label: getRoleLabel(role),
    value: role.Id,
  }));

  return (
    <Form className="form-horizontal">
      <PorAccessManagementUsersSelector
        options={availableUsersAndTeams as Option[]}
        value={values.selectedUsersAndTeams as Option[]}
        onChange={(value) => setFieldValue('selectedUsersAndTeams', value)}
        label="User/team"
        placeholder="Select one or more users and/or teams"
        isLoading={isLoading}
      />

      <FormControl label="Role" inputId="role-selector">
        <PortainerSelect
          inputId="role-selector"
          value={values.selectedRoleId}
          onChange={(roleId) => setFieldValue('selectedRoleId', roleId)}
          options={roleOptions}
          data-cy="access-management-role-select"
          isLoading={isLoading}
          loadingMessage={() => 'Loading roles...'}
          noOptionsMessage={() => 'No roles available.'}
        />
      </FormControl>

      <div className="form-group">
        <div className="col-sm-12">
          <LoadingButton
            type="submit"
            className="!ml-0"
            disabled={
              availableUsersAndTeams.length === 0 ||
              values.selectedUsersAndTeams.length === 0 ||
              !dirty ||
              !isValid
            }
            isLoading={isUpdating}
            loadingText="Creating access..."
            icon={Plus}
            data-cy="access-createAccess"
          >
            Create access
          </LoadingButton>
        </div>
      </div>
    </Form>
  );

  function getRoleLabel(role: Role) {
    if (!limitedToBE) {
      return role.Name;
    }

    if (role.Id !== RoleTypes.STANDARD) {
      return `${role.Name} (Business Edition Feature)`;
    }

    return `${role.Name} (Default)`;
  }
}
